import { useState } from "react";
import { api, formatApiErrorDetail } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";

export default function DangerZone({ onReset }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  if (user?.role !== "admin") return null;

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reset-consumer-data", { confirm });
      const n = Object.values(data.deleted).reduce((a, b) => a + b, 0);
      toast.success(`Fresh start — removed ${n} records (consumers, notes, plans, drafts).`);
      setOpen(false);
      setConfirm("");
      onReset?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="danger-zone" className="mt-8 rounded-2xl border border-[#F0D5CE] bg-[#FDF7F5] p-5">
      <p className="text-[10px] font-bold text-[#B0503C] uppercase tracking-[0.2em] flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" strokeWidth={2} /> Administrator · danger zone</p>
      <div className="mt-2 flex items-center justify-between gap-4 flex-wrap">
        <p className="text-sm text-[#3A453F] max-w-xl">Start fresh: permanently delete <b>every</b> consumer, PCP, progress note, weekly plan and draft. Staff logins are kept. This cannot be undone.</p>
        <Button data-testid="reset-data-btn" variant="outline" onClick={() => setOpen(true)} className="rounded-full border-[#B0503C] text-[#B0503C] bg-white hover:bg-[#B0503C] hover:text-white h-10">
          <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.5} /> Delete all consumer data
        </Button>
      </div>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setConfirm(""); }}>
        <DialogContent data-testid="reset-data-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-[#B0503C]">Delete all consumer data?</DialogTitle>
            <DialogDescription>Type <b>DELETE ALL</b> to confirm. Consumers, PCPs, notes, weekly plans and drafts will be gone for good. Staff accounts stay.</DialogDescription>
          </DialogHeader>
          <Input data-testid="reset-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="DELETE ALL" className="h-12 bg-white border-[#E2DFD8] font-mono" />
          <DialogFooter>
            <Button data-testid="reset-cancel-btn" variant="ghost" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
            <Button data-testid="reset-confirm-btn" onClick={run} disabled={busy || confirm.trim() !== "DELETE ALL"} className="rounded-full bg-[#B0503C] hover:bg-[#8f3f2f] text-white h-11 px-6">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.5} />} Yes, delete everything
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
